import { useMemo } from "react"
import { format, isValid, parseISO } from "date-fns"

import { Button, Select } from "@productlane/ui"

interface ChangelogDateStepProps {
	data: string[][]
	hasHeaders: boolean
	back: () => void
	next: () => void
	dateIndex: number
	setDateIndex: (i: number) => void
	sortedData: string[][]
}

function parseDate(dateString: string): Date | null {
	// Try parsing as ISO format first
	const isoDate = parseISO(dateString)
	if (isValid(isoDate)) {
		return isoDate
	}

	// Try parsing with native Date constructor (handles various formats)
	const nativeDate = new Date(dateString)
	if (isValid(nativeDate)) {
		return nativeDate
	}

	return null
}

export function ChangelogDateStep({
	data,
	hasHeaders,
	back,
	next,
	dateIndex,
	setDateIndex,
	sortedData,
}: ChangelogDateStepProps) {
	const headers = useMemo(() => {
		if (!data[0]) {
			return []
		}
		const options = hasHeaders
			? data[0].map((it, i) => ({
					label: it,
					value: i.toString(),
				}))
			: data[0].map((_, i) => ({
					label: `${i + 1}. Column`,
					value: i.toString(),
				}))

		return [{ label: "None (use today's date)", value: "-1" }, ...options]
	}, [hasHeaders, data])

	return (
		<div>
			<Select
				value={dateIndex.toString()}
				name="dateIndex"
				label="Select the column with the date (optional)"
				options={headers}
				onChange={(it) => setDateIndex(Number(it))}
				className="px-[1px]"
			/>

			{dateIndex !== -1 && (
				<>
					<div className="mb-2 mt-4 text-sm text-primary">Preview:</div>
					<div className="relative mt-1 overflow-hidden rounded-lg border bg-white p-2 dark:bg-gray-900">
						{sortedData[dateIndex]
							?.slice(hasHeaders ? 1 : 0, hasHeaders ? 5 : 4)
							.map((it, i) => {
								const parsedDate = parseDate(it)
								return (
									<div className="border-b p-1" key={i}>
										{parsedDate ? (
											<>
												<span className="text-sm text-primary">
													{format(parsedDate, "MMM d, yyyy")}
												</span>
												<span className="ml-2 text-xs text-secondary">
													({it})
												</span>
											</>
										) : (
											<span className="text-red-600">Invalid date: {it}</span>
										)}
									</div>
								)
							})}
						<div className="absolute inset-x-0 bottom-0 h-[80%] bg-gradient-to-b from-transparent via-white/80 to-white dark:via-brand-900/80 dark:to-brand-900" />
					</div>
				</>
			)}

			<div className="mt-4 flex justify-between">
				<Button variant="ghost" onClick={back}>
					Back
				</Button>
				<Button onClick={next}>Next</Button>
			</div>
		</div>
	)
}
